import { App, AppBlock, AppStatusError, AuthenticationError, AuthorizationError, BlockedResourceError, InvalidInputError, NetworkError, NotFoundError, Plan, RepositoryBuildInfo, Subscription, User } from "@back4app2/sdk";
import { useEffect, useMemo, useReducer, useState, useCallback } from "react";
import { useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import back4app2 from "../back4app2";
import { hasReadPermission, hasWritePermission } from "../utils/permissions";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorMessage from "../components/ErrorMessage";
import TextInput from "../components/FormInputField/TextInput/TextInput";
import { BACK4APP_DOT_COM_SITE_URL, BACK4APP_DOT_COM_DASHBOARD_URL } from "../settings";
import RuntimeVersionField, { isValidRuntimePin } from "../components/BuildMethodSelector/RuntimeVersionField";
import ToggleSwitchCheckbox from "../components/FormInputField/ToggleSwitchCheckbox/ToggleSwitchCheckbox";
import toast from "react-hot-toast";
import { ReactComponent as PlusIcon } from '../assets/images/plus-icon.svg';
import { ReactComponent as TrashIcon } from "../assets/images/trash-icon.svg";
import { ReactComponent as VisibilityIcon } from "../assets/images/visibility-icon.svg";
import { ReactComponent as VisibilityOffIcon } from "../assets/images/visibility-off-icon.svg";
import { ReactComponent as AttentionIconSVG } from "../assets/images/circle-exclaimation-icon.svg";
import { ReactComponent as StatusErrorSVG } from '../assets/images/status-error.svg';
import { ReactComponent as StatusSuccessSVG } from '../assets/images/status-success.svg';
import { ReactComponent as StatusSpinnerSVG } from '../assets/images/status-spinner.svg';
import { ReactComponent as CopySVG } from '../assets/images/copy-icon.svg';
import Button from "../components/Button";
import Modal from "../components/Modal";
import CustomDomainSettings from "../components/CustomDomainSettings";
import BuildMethodSelector, { BuildMethod } from "../components/BuildMethodSelector/BuildMethodSelector";
import RepositoryBrowser, { ROOT_PATH } from "../components/RepositoryBrowser/RepositoryBrowser";
import { ReactComponent as FolderIcon } from '../assets/images/folder-icon.svg';
import ReactTooltip from "react-tooltip";
import { AmplitudeEvent, trackEvent } from "../utils/amplitude";
import { pushPaddlePurchaseEvent } from "../utils/gtm";
import { initializePaddle, InitializePaddleOptions } from '@paddle/paddle-js';
import Collaborators from "../components/Collaborators/Collaborators";
import GhostMessage from "../components/GhostMessage";
import { DsButton, Eyebrow, SettingsSaveBar, SettingsSection, SurfaceCard } from '../components/ds';
import { TOAST_SURFACE } from '../components/ds/toast';

enum MyAppSettingsTab {
  GENERAL_SETTINGS = 'general-settings',
  BUILD_AND_DEPLOY = 'build-and-deploy',
  VARIABLES = 'variable',
  CUSTOM_DOMAIN = 'custom-domain',
  HEALTH_AND_ALERT = 'health-and-alert',
  SUSPEND_DELETE = 'suspend-delete'
}

enum MyAppSettingsFieldName {
  BRANCH_NAME,
  ROOT_DIR,
  AUTO_DEPLOY,
  ENV_VARS,
  EXPOSED_PORT,
  HEALTH_CHECK_ENDPOINT,
  COLLABORATOR_EMAIL
}

interface MyAppSettingsState {
  currentTab: MyAppSettingsTab;
  isLoadingApp: boolean;
  appLoadingErrorMessage?: string;
  app?: App;
  isSubmitting: boolean;
  submittingErrorMessage?: string;
  isTriggerDeploymentModalOpen: boolean;
  plans?: Plan[];
  isLoadingPlans: boolean;
  loadingPlansErrorMessage?: string;
  // Paddle-related state
  isLoadingPaddle: boolean;
  paddleError?: string;
  paddle?: any;
  openCheckout: boolean;
  selectedPlan?: Plan;
}

const INITIAL_STATE: MyAppSettingsState = {
  currentTab: MyAppSettingsTab.GENERAL_SETTINGS,
  isLoadingApp: true,
  isSubmitting: false,
  isTriggerDeploymentModalOpen: false,
  isLoadingPlans: true,
  // Paddle-related initial state
  isLoadingPaddle: false,
  openCheckout: false,
};

enum MyAppSettingsActionType {
  RESET,
  FINISH_LOADING_APP,
  SET_CURRENT_TAB,
  SET_TRIGGER_DEPLOYMENT_MODAL,
  FINISH_UPDATING_APP,
  FINISH_LOADING_PLANS,
  // Paddle-related actions
  SET_PADDLE_LOADING,
  FINISH_LOADING_PADDLE,
  SET_CHECKOUT_MODAL,
  SET_SELECTED_PLAN,
}

const reset = () => ({
  type: MyAppSettingsActionType.RESET
} as const);

const setCurrentTab = (tab: MyAppSettingsTab) => ({
  type: MyAppSettingsActionType.SET_CURRENT_TAB,
  payload: tab
} as const);

const finishLoadingApp = (errorMessage?: string, app?: App) => ({
  type: MyAppSettingsActionType.FINISH_LOADING_APP,
  payload: {
    errorMessage,
    app
  }
} as const);

const finishUpdatingApp = (app: App, errorMessage?: string) => ({
  type: MyAppSettingsActionType.FINISH_UPDATING_APP,
  payload: {
    errorMessage,
    app
  }
} as const);

const setPaddleLoading = (isLoading: boolean) => ({
  type: MyAppSettingsActionType.SET_PADDLE_LOADING,
  payload: isLoading,
} as const);

const finishLoadingPlans = (errorMessage?: string, plans?: Plan[]) => ({
  type: MyAppSettingsActionType.FINISH_LOADING_PLANS,
  payload: {
    errorMessage,
    plans
  }
} as const);

const openTriggerDeploymentModal = () => ({
  type: MyAppSettingsActionType.SET_TRIGGER_DEPLOYMENT_MODAL,
  payload: true,
} as const);

const closeTriggerDeploymentModal = () => ({
  type: MyAppSettingsActionType.SET_TRIGGER_DEPLOYMENT_MODAL,
  payload: false,
} as const);

// Paddle-related action creators
const finishLoadingPaddle = (errorMessage?: string, paddle?: any) => ({
  type: MyAppSettingsActionType.FINISH_LOADING_PADDLE,
  payload: {
    errorMessage,
    paddle,
  },
} as const);

const setCheckoutModal = (isOpen: boolean) => ({
  type: MyAppSettingsActionType.SET_CHECKOUT_MODAL,
  payload: isOpen,
} as const);

const setSelectedPlan = (plan?: Plan) => ({
  type: MyAppSettingsActionType.SET_SELECTED_PLAN,
  payload: plan,
} as const);

type MyAppSettingsAction = ReturnType<typeof reset> | ReturnType<typeof finishLoadingApp> | ReturnType<typeof setCurrentTab> | ReturnType<typeof openTriggerDeploymentModal> | ReturnType<typeof closeTriggerDeploymentModal> | ReturnType<typeof finishUpdatingApp> | ReturnType<typeof finishLoadingPlans> | ReturnType<typeof setPaddleLoading> | ReturnType<typeof finishLoadingPaddle> | ReturnType<typeof setCheckoutModal> | ReturnType<typeof setSelectedPlan>;

const reducer = (state: MyAppSettingsState = INITIAL_STATE, action: MyAppSettingsAction): MyAppSettingsState => {
  switch (action.type) {
    case MyAppSettingsActionType.RESET:
      return INITIAL_STATE;
    
    case MyAppSettingsActionType.FINISH_LOADING_APP:
      return {
        ...state,
        isLoadingApp: false,
        appLoadingErrorMessage: action.payload.errorMessage,
        app: action.payload.app
      }
    case MyAppSettingsActionType.SET_CURRENT_TAB: {
      return {
        ...state,
        currentTab: action.payload || MyAppSettingsTab.GENERAL_SETTINGS
      }
    }
    case MyAppSettingsActionType.SET_TRIGGER_DEPLOYMENT_MODAL:
      return {
        ...state,
        isTriggerDeploymentModalOpen: action.payload
      }
    case MyAppSettingsActionType.FINISH_UPDATING_APP:
      return {
        ...state,
        app: action.payload.app,
        isSubmitting: false,
        submittingErrorMessage: action.payload.errorMessage
      }
    case MyAppSettingsActionType.FINISH_LOADING_PLANS:
      return {
        ...state,
        plans: action.payload.plans,
        isLoadingPlans: false,
        loadingPlansErrorMessage: action.payload.errorMessage
      }
    case MyAppSettingsActionType.SET_PADDLE_LOADING:
      return {
        ...state,
        isLoadingPaddle: action.payload
      }
    case MyAppSettingsActionType.FINISH_LOADING_PADDLE:
      return {
        ...state,
        paddle: action.payload.paddle,
        paddleError: action.payload.errorMessage,
        isLoadingPaddle: false,
      }
    case MyAppSettingsActionType.SET_CHECKOUT_MODAL:
      return {
        ...state,
        openCheckout: action.payload
      }
    case MyAppSettingsActionType.SET_SELECTED_PLAN:
      return {
        ...state,
        selectedPlan: action.payload
      }
  }
};

type AppForm = {
  branchName?: string, 
  rootDir?: string, 
  envs?: { key: string; value?: string }[], 
  autoDeploy?: boolean,
  exposedPort?: number,
  healthCheckEndpoint?: string,
  startCommand?: string,
  builderPreference?: string,
  runtimeVersion?: string
}

const ENV_VARIABLE_MAX_LENGTH = 1023;

const MyAppSettings = () => {
  const { appId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { updateIsDeletingApp, user } = useOutletContext<{ updateIsDeletingApp: (isDeleting: boolean) => void, user: User }>();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const [isFreePlan, setisFreePlan] = useState(true); // Default to true to be safe
  const { currentTab, app, isLoadingApp, appLoadingErrorMessage, isSubmitting, isTriggerDeploymentModalOpen, plans, isLoadingPlans, loadingPlansErrorMessage, paddle, openCheckout } = state;
  const mainServiceEnvironment = app && app.mainService && app.mainService.mainServiceEnvironment;
  const mainServiceEnvironmentId = mainServiceEnvironment && mainServiceEnvironment.id;

  useEffect(() => {
    dispatch(setCurrentTab(location.hash.replace('#', '') as MyAppSettingsTab));
  }, [location.hash]);

  useEffect(() => {
    let appSubscription: Subscription;
    (async() => {

      if (appId) {
        try {
          const app: App = await back4app2.findAppById(appId);
          dispatch(finishLoadingApp(undefined, app));
        } catch (err) {
          let errMsg = '';
          if (err instanceof NetworkError) {
            console.error('network error', err);
            errMsg = 'Network error when fetching app settings. Check your internet connection and try again.';
          } else if (err instanceof NotFoundError) {
            errMsg = 'App not found!'
          } else {
            console.error('unexpected error fetching app settings', err);
            errMsg = 'Unexpected error while fetching app settings. Please try again.'
          }
          dispatch(finishLoadingApp(errMsg, undefined));
        }

        appSubscription = back4app2.subscribeToApp(appId, (error, snapshot) => {
          if (error) {
            setisFreePlan(true);
          } else if (snapshot) {
            // Check if the app is on a free plan
            const mainServiceEnvironment = snapshot.mainService && snapshot.mainService.mainServiceEnvironment;
            const isPendingPayment = mainServiceEnvironment && (
              mainServiceEnvironment.isPendingPayment || 
              (!mainServiceEnvironment.isFreePlanElegible && mainServiceEnvironment.plan.name.includes('Free'))
            );
            const isFree = mainServiceEnvironment?.plan.name.includes('Free') || false;
            setisFreePlan(isFree || !!isPendingPayment);
          }
        });
      }
    })();

    return () => {
      if (appSubscription) {
        appSubscription.unsubscribe();
      }
    }
  }, [appId]);

  useEffect(() => {
    (async() => {
      try {
        const plans = await back4app2.getPlans();
        dispatch(finishLoadingPlans(undefined, plans));
      } catch (err) {
        let errMsg = '';
        if (err instanceof NetworkError) {
          console.error('network error', err);
          errMsg = 'Network error when fetching plans. Check your internet connection and try again.';
        } else {
          console.error('unexpected error fetching plans', err);
          errMsg = 'Unexpected error while fetching plans. Please try again.'
        }
        dispatch(finishLoadingPlans(errMsg));
      }
    })();
  }, []);

  const loadPaddle = useCallback(() => {
    const paddleOptions = {
      token: process.env.REACT_APP_PADDLE_TOKEN || 'test_0270ab179b4f4abd7aa228c7014',
      environment: process.env.REACT_APP_SENTRY_ENV === 'production' ? 'production' : 'sandbox',
      pwCustomer: {}
    };

    const paddleEventCallback = async function (data: any) {
      if (data.name === 'checkout.completed') {
        pushPaddlePurchaseEvent(data.data);

        const paymentData = {
          appId: data.data.custom_data.app_id,
          customerId: data.data.customer.id,
          planName: data.data.items[0].product.name,
          transactionId: data.data.transaction_id,
          checkoutId: data.data.id,
          results: data.data,
          planId: data.data.custom_data.plan_id,
          email: data.data.customer.email,
        };

        // Save subscription
        try {
          await fetch(`${process.env.REACT_APP_BACK4APP_CHECKOUT_URL}/save-subscription`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(paymentData),
          });
        } catch (error) {
          console.error('Error saving subscription:', error);
          toast.error('Error updating plan information, please contact support!');
        }

        await new Promise(resolve => setTimeout(resolve, 1000));

        dispatch(setCheckoutModal(false));
        dispatch(setSelectedPlan(undefined));

        toast.success('Plan updated successfully!', {
          className: TOAST_SURFACE,
          icon: <StatusSuccessSVG width="24px" height="24px" className="animate-bounce-in" />,
          duration: 6000
        });

        await new Promise(resolve => setTimeout(resolve, 1000));

        navigate(0);
      }
      if (data.name === 'checkout.closed') {
        dispatch(setCheckoutModal(false));
        dispatch(setSelectedPlan(undefined));
      }
    };

    initializePaddle({ ...paddleOptions, eventCallback: paddleEventCallback } as InitializePaddleOptions).then(
      (paddleInstance: any) => {
        if (paddleInstance) {
          dispatch(finishLoadingPaddle(undefined, paddleInstance));
        }
      },
    ).catch((error) => {
      console.error('Paddle initialization failed:', error);
      dispatch(finishLoadingPaddle('Failed to initialize Paddle', undefined));
      toast.error('Failed to open checkout, please contact support!', {
        className: TOAST_SURFACE,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000
      });
    });
  }, [navigate]);

  // Paddle initialization
  useEffect(() => {
    loadPaddle();
  }, [loadPaddle]);

  const handleOnClickPlan = async (plan: Plan) => {
    if (plan.name === 'Free') {
      toast.error('Please contact support for Free plan!', {
        className: TOAST_SURFACE,
        icon: <AttentionIconSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 4000
      });
      return;
    }
    dispatch(setSelectedPlan(plan));
    dispatch(setCheckoutModal(true));

    // Wait for the modal to be rendered before opening Paddle checkout
    setTimeout(async () => {
      if (paddle) {
        const paddlePriceId = process.env.REACT_APP_SENTRY_ENV === 'production' ? plan.checkoutPriceKey : 'pri_01jj29jwpxef927zy0waqcdtjh';

        let userEmail = user.username;

        if (app && !app.isOwner) {
          userEmail = await back4app2.getAppOwnerEmail(app.id);
        }
        
        try {
          paddle.Checkout.open({
            items: [{ priceId: paddlePriceId, quantity: 1 }],
            title: plan.name,
            settings: {
              displayMode: 'inline',
              theme: 'light',
              locale: 'en',
              variant: 'one-page',
              frameTarget: 'checkout-container',
              frameInitialHeight: '450',
              frameStyle: 'width: 100%; min-width: 312px; max-height: 80vh; background-color: #f9f9f9; border: none;'
            },
            customData: { appId: appId, planId: plan.checkoutPlanId },
            allowLogout: false,
            customer: {
              email: userEmail,
            }
          });
        } catch (error) {
          console.error('Error opening Paddle checkout inline:', error);
          try {
            paddle.Checkout.open({
              items: [{ priceId: paddlePriceId, quantity: 1 }],
              title: plan.name,
              customData: { appId: appId, planId: plan.checkoutPlanId },
              allowLogout: false,
              customer: {
                email: user.username,
              }
            });
          } catch (fallbackError) {
            console.error('Fallback Paddle checkout also failed:', fallbackError);
          }
        }
      } else {
        console.error('Paddle instance not available');
      }
    }, 200); // Increased delay to ensure modal is fully rendered
  };

  const handleSubmit = async (newValues: AppForm) => {
    // check if something change
    let isSettingsUpdated = false;
    if (newValues.branchName && newValues.branchName !== app?.mainService?.mainServiceEnvironment?.branchName) {
      isSettingsUpdated = true;
    }
    if (newValues.rootDir && newValues.rootDir !== app?.mainService?.mainServiceEnvironment?.rootDirPath) {
      isSettingsUpdated = true;
    }
    if (typeof newValues.autoDeploy !== "undefined" && newValues.autoDeploy !== app?.mainService?.mainServiceEnvironment?.autoDeploy) {
      isSettingsUpdated = true;
    }
    if (newValues.envs) {
      if (app?.mainService?.mainServiceEnvironment?.envVars.length !== newValues.envs.length) {
        isSettingsUpdated = true;
      } else {
        // check each envs
        for (let idx = 0; idx < newValues.envs.length; idx++) {
          const newEnv = newValues.envs[idx];
          const sameEnv = app.mainService.mainServiceEnvironment.envVars.find(oldEnv => {
            if (oldEnv.key === newEnv.key && oldEnv.value === newEnv.value) return false;
            else if (oldEnv.key === newEnv.key && oldEnv.value !== newEnv.value) return true;
            else if (oldEnv.key !== newEnv.key && oldEnv.value === newEnv.value) return true;
            return false;
          });
          if (sameEnv) {
            isSettingsUpdated = true; break;
          }
        }
      }
    }
    if (newValues.exposedPort !== app?.mainService?.mainServiceEnvironment?.exposedPort) {
      isSettingsUpdated = true;
    }
    if (newValues.startCommand !== undefined && newValues.startCommand !== (app?.mainService?.mainServiceEnvironment?.startCommand || '')) {
      isSettingsUpdated = true;
    }
    // NULL reads as DOCKERFILE, so an app that never had the setting is only
    // "updated" when the user actually moves off Dockerfile.
    if (newValues.builderPreference !== undefined && newValues.builderPreference !== (app?.mainService?.mainServiceEnvironment?.builderPreference || 'DOCKERFILE')) {
      isSettingsUpdated = true;
    }
    if (newValues.runtimeVersion !== undefined && newValues.runtimeVersion !== (app?.mainService?.mainServiceEnvironment?.runtimeVersion || '')) {
      isSettingsUpdated = true;
    }
    if (newValues.healthCheckEndpoint && newValues.healthCheckEndpoint !== app?.mainService?.mainServiceEnvironment?.healthCheckEndpoint) {
      isSettingsUpdated = true;
    }

    if (!isSettingsUpdated) {
      toast('Please make some changes to save new settings!', {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-md text-sm",
        icon: <AttentionIconSVG width="20px" height="20px" className="animate-bounce-in text-old-blue" />
      });
      return;
    }

    const toastId = toast.loading('Updating settings...', {
      className: TOAST_SURFACE,
      icon: <StatusSpinnerSVG width="24px" height="24px" color="text-old-blue" className="animate-spin" />
    });
    try {
      const updatedApp = await back4app2.updateAppSettings(appId as string, newValues.branchName, newValues.rootDir, newValues.autoDeploy, newValues.envs, newValues.healthCheckEndpoint, newValues.exposedPort, newValues.startCommand, newValues.builderPreference, newValues.runtimeVersion);
      dispatch(finishUpdatingApp(updatedApp, undefined));
      toast.success('Settings were updated!', {
        className: TOAST_SURFACE,
        id: toastId,
        icon: <StatusSuccessSVG width="24px" height="24px" className="animate-bounce-in top-" />
      });
      // Only show deployment modal if user has write permission for Deploy
      if (updatedApp && hasWritePermission(updatedApp, 'Deploy')) {
        dispatch(openTriggerDeploymentModal());
      }
    } catch (err) {
      let errMsg = 'Something went wrong!';
      // InvalidInputError carries a message written for this exact field (a
      // traversing Root Directory, a broken Start Command). Falling through to
      // the generic toast throws away the only thing that tells the user what
      // to change.
      if (err instanceof AppStatusError || err instanceof AuthorizationError || err instanceof InvalidInputError) {
        errMsg = err.message;
      } else {
        console.error('error', err);
      }
      toast.error(errMsg, {
        className: TOAST_SURFACE,
        id: toastId,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000
      });
      //
    }
  }

  const getFormContent = () => {
    let formContent;
    if (!app) {
      return;
    }
    switch (currentTab) {
      case MyAppSettingsTab.GENERAL_SETTINGS: {
        if (!hasReadPermission(app, 'General')) {
          return null;
        }
        formContent = <GeneralSettingsForm currentUser={user} appId={app.id} appName={app.name} plan={app.mainService?.mainServiceEnvironment?.plan} plans={plans} isLoadingPlans={isLoadingPlans} loadingPlansErrorMessage={loadingPlansErrorMessage} onPlanClick={handleOnClickPlan} isOwner={app.isOwner} />;
        break;
      }
      case MyAppSettingsTab.BUILD_AND_DEPLOY: {
        formContent = <BuildAndDeployForm
          sourceKind={app.mainService?.mainServiceEnvironment?.sourceKind || 'GITHUB'}
          imageRepository={app.mainService?.mainServiceEnvironment?.imageRepository || ''}
          imageTag={app.mainService?.mainServiceEnvironment?.imageTag || ''}
          branchName={app.mainService?.mainServiceEnvironment?.branchName || ''}
          rootDir={app.mainService?.mainServiceEnvironment?.rootDirPath || ''}
          repositoryId={app.mainService?.repository?.id || ''}
          autoDeploy={!!app.mainService?.mainServiceEnvironment?.autoDeploy}
          exposedPort={`${app.mainService?.mainServiceEnvironment?.exposedPort || ''}`}
          startCommand={app.mainService?.mainServiceEnvironment?.startCommand || ''}
          isStaticSite={!!app.mainService?.mainServiceEnvironment?.isStaticSite}
          builderPreference={app.mainService?.mainServiceEnvironment?.builderPreference || ''}
          runtimeVersion={app.mainService?.mainServiceEnvironment?.runtimeVersion || ''}
          isSubmitting={isSubmitting}
          handleSubmit={handleSubmit}
          plan={app.mainService?.mainServiceEnvironment?.plan}
          isFreePlan={isFreePlan}
          canWrite={hasWritePermission(app, 'BuildAndDeploy')}
          canRead={hasReadPermission(app, 'BuildAndDeploy')}
        />;
        break;
      }
      case MyAppSettingsTab.VARIABLES: {
        formContent = <VariablesForm envVars={app.mainService?.mainServiceEnvironment?.envVars || []} isSubmitting={isSubmitting} handleSubmit={handleSubmit} canWrite={hasWritePermission(app, 'EnvVars')} canRead={hasReadPermission(app, 'EnvVars')} />;
        break;
      }
      case MyAppSettingsTab.CUSTOM_DOMAIN: {
        formContent = <CustomDomainSettings 
          appId={app.id}
          serviceEnvironmentId={mainServiceEnvironmentId as string} 
          mainCustomDomainId={app.mainService?.mainServiceEnvironment?.mainCustomDomain?.id as string}
          onClickUpgrade={() => dispatch(setCurrentTab(MyAppSettingsTab.GENERAL_SETTINGS))}
          canRead={hasReadPermission(app, 'Domain')}
          canWrite={hasWritePermission(app, 'Domain')}
          canDeploy={hasWritePermission(app, 'Deploy')}
        />
        break;
      }
      case MyAppSettingsTab.HEALTH_AND_ALERT: {
        formContent = <HealthAndAlertForm healthCheckEndpoint={app.mainService?.mainServiceEnvironment?.healthCheckEndpoint || ''}
          isSubmitting={isSubmitting}
          handleSubmit={handleSubmit}
          canWrite={hasWritePermission(app, 'HealthAndAlert')}
          canRead={hasReadPermission(app, 'HealthAndAlert')}
        />
        break;
      }
      case MyAppSettingsTab.SUSPEND_DELETE: {
        formContent = <SuspendDeleteForm appName={app.name} appId={app.id} block={app.block} updateIsDeletingApp={updateIsDeletingApp} />;
        break;
      }
      default:
        break;
    }
    if (!app.mainService?.mainServiceEnvironment?.id && ![MyAppSettingsTab.GENERAL_SETTINGS, MyAppSettingsTab.SUSPEND_DELETE].includes(currentTab)) {
      formContent = (
        <div className="rounded-card border border-navy-500 bg-navy-800 px-4 py-10 text-center text-ds-135 text-navy-300">
          Nothing here, yet.
        </div>
      );
    }
    return formContent;
  }

  const handleTriggerDeployment = async () => {
    if (!app) {
      toast.error('App information is not available.', {
        className: TOAST_SURFACE,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 4000
      });
      dispatch(closeTriggerDeploymentModal());
      return;
    }

    if (!hasWritePermission(app, 'Deploy')) {
      toast.error('You don\'t have permission to trigger deployments.', {
        className: TOAST_SURFACE,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 4000
      });
      dispatch(closeTriggerDeploymentModal());
      return;
    }

    const mainServiceEnvironment = app.mainService && app.mainService.mainServiceEnvironment;
    const mainServiceEnvironmentId = mainServiceEnvironment && mainServiceEnvironment.id;

    try {
      await back4app2.triggerManualDeployment(mainServiceEnvironmentId as string);
      dispatch(closeTriggerDeploymentModal());
      navigate(`/apps/${appId}`);
    } catch (err) {
      let errMsg = 'Something went wrong while triggering new deployment!';
      if (err instanceof NetworkError) {
        errMsg = 'Network error when triggering deployment. Check your internet connection and try again.';
      } else if (err instanceof AuthenticationError) {
        window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
        return;
      } else if (err instanceof AppStatusError || err instanceof NotFoundError || err instanceof AuthorizationError) {
        errMsg = err.message;
      } else {
        console.error('error triggering deployment', err);
      }
      toast.error(errMsg, {
        className: TOAST_SURFACE,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000
      });
    }
  }

  // One place naming each section, so the head, the tab and the browser hash
  // cannot drift apart.
  const TABS: {
    id: MyAppSettingsTab,
    label: string,
    title: string,
    blurb: string,
    count?: number,
    danger?: boolean,
    ownerOnly?: boolean,
  }[] = [
    {
      id: MyAppSettingsTab.GENERAL_SETTINGS,
      label: 'General',
      title: 'General',
      blurb: 'Name, plan, and who else can work on this app.',
    },
    {
      id: MyAppSettingsTab.BUILD_AND_DEPLOY,
      label: 'Build & deploy',
      title: 'Build & deploy',
      blurb: 'Where the code comes from, how it is built, and when it deploys.',
    },
    {
      id: MyAppSettingsTab.VARIABLES,
      label: 'Environment',
      title: 'Environment variables',
      blurb: 'Available at build time and at runtime. A change needs a redeploy.',
      count: app?.mainService?.mainServiceEnvironment?.envVars?.length,
    },
    {
      id: MyAppSettingsTab.CUSTOM_DOMAIN,
      label: 'Domains',
      title: 'Domains',
      blurb: 'Where your app answers. SSL is issued automatically.',
    },
    {
      id: MyAppSettingsTab.HEALTH_AND_ALERT,
      label: 'Health & alerts',
      title: 'Health & alerts',
      blurb: 'The path Back4app checks before releasing a deploy.',
    },
    {
      id: MyAppSettingsTab.SUSPEND_DELETE,
      label: 'Danger zone',
      title: 'Danger zone',
      blurb: 'Permanently delete this web app.',
      danger: true,
      ownerOnly: true,
    },
  ];

  const visibleTabs = TABS.filter(tab => !tab.ownerOnly || (app && app.isOwner));
  const activeTab = visibleTabs.find(tab => tab.id === currentTab) || visibleTabs[0];

  const idPill = (
    <div className="inline-flex items-center gap-2 h-[30px] px-3 rounded-control border border-navy-500 bg-navy-700 font-courrier text-ds-115 text-navy-200">
      <span className="uppercase tracking-[0.12em] text-navy-300">id</span>
      <span className="text-navy-100 truncate max-w-[16ch]" title={appId}>{appId}</span>
      <button
        type="button"
        aria-label="Copy deployment ID"
        className="b4-focus rounded-[3px] text-navy-300 hover:text-white"
        data-tip="Copied!"
        data-event="click focus"
        onClick={() => navigator.clipboard.writeText(appId as string)}
      >
        <CopySVG className="flex-none" />
      </button>
    </div>
  );

  return (
    <>
      {/* The tab row sits on its own lighter ground, bleeding to the edges of
          the page. That change of surface is what separates "this names the
          section" from "this is the section" — the hairline alone was too
          quiet to read as a division. `-mx` cancels `main`'s padding exactly,
          so the text still lines up with everything else. */}
      <div className="-mx-4 lg:-mx-8 flex flex-col">
        {/* The ID sat beside the title and, on a phone, literally on top of
            it. It is metadata you copy once, so it drops under the head. */}
        <div className="px-4 lg:px-8 pb-5 border-b border-navy-700 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
          <div className="flex flex-col gap-2 min-w-0">
            <Eyebrow>settings</Eyebrow>
            <h1 className="font-sora text-ds-20 font-semibold tracking-[-0.02em] leading-[1.2] text-white">
              {activeTab.title}
            </h1>
            <p className="m-0 text-ds-135 leading-[1.5] text-navy-200">{activeTab.blurb}</p>
          </div>
          <div className="flex-none">{idPill}</div>
        </div>

        {/* Scrolls sideways on a phone rather than wrapping to three rows. */}
        <div className="px-4 lg:px-8 py-2.5 bg-navy-800 border-b border-navy-700 overflow-x-auto b4-noscroll">
          <ul className="flex items-center gap-1 w-max lg:w-full" role="tablist">
            {visibleTabs.map(tab => {
              const isActive = tab.id === activeTab.id;
              return (
                <li key={tab.id}>
                  <a
                    href={`#${tab.id}`}
                    role="tab"
                    aria-selected={isActive}
                    className={[
                      'b4-focus flex items-center gap-1.5 h-9 px-3 rounded-control text-ds-135 whitespace-nowrap',
                      'transition-colors duration-[120ms]',
                      isActive
                        ? 'bg-navy-600 text-white font-semibold'
                        : tab.danger
                          ? 'text-state-err font-medium hover:bg-navy-600/50'
                          : 'text-navy-200 font-medium hover:text-white hover:bg-navy-600/50',
                    ].join(' ')}
                  >
                    {tab.label}
                    {tab.count ? (
                      <span className="font-courrier text-ds-105 text-navy-300">{tab.count}</span>
                    ) : null}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div>
        {isLoadingApp ? <div className="flex h-full justify-center items-center"> <LoadingSpinner color='text-white' /> </div> : null}
        {appLoadingErrorMessage ? (<>
          <div className="flex h-full">
            <ErrorMessage message={appLoadingErrorMessage} />
          </div>
        </>) : (getFormContent())}
      </div>
      <Modal open={isTriggerDeploymentModalOpen} onClose={() => dispatch(closeTriggerDeploymentModal())}>
        <>
          <div className="flex w-full justify-center">
            <StatusSuccessSVG className="text-cta-green" width="40px" height="56px" />
          </div>
          <div className="font-semibold leading-140 text-dark text-center mb-2">Settings updated successfully</div>
          <div className="text-sm text-center max-w-xs mb-2">Would you like trigger new deployment with saved settings?</div>
          <div className="flex justify-center space-x-2 w-full mt-4">
            <Button className="text-dark-grey border border-light-grey rounded-[5px]" onClick={() => dispatch(closeTriggerDeploymentModal())}>No</Button>
            <Button 
              type="primary" 
              onClick={handleTriggerDeployment}
              isDisabled={!app || !hasWritePermission(app, 'Deploy')}
            >
              Yes
            </Button>
          </div>
        </>
      </Modal>
      <ReactTooltip
        className="tooltip shadow-[0px_0px_30px_rgba(0,0,0,0.4)]" 
        delayHide={2000} 
        place="top" 
        effect="solid" 
        html={true} 
        arrowColor="#303338" 
        eventOff="click" 
        afterShow={() => navigator.clipboard.writeText(appId as string)}
      />
      
      {/* Paddle Checkout Modal */}
      {openCheckout && (
        <Modal
          open={openCheckout}
          onClose={() => dispatch(setCheckoutModal(false))}
        >
          <div className="w-full max-w-4xl mx-auto">
            <div 
              id="checkout-container" 
              className="checkout-container w-full min-h-[450px] bg-white rounded-lg"
              style={{ minHeight: '450px' }}
            ></div>
          </div>
        </Modal>
      )}
    </>
  )
};

const GeneralSettingsForm = (props: { currentUser: User, appId: string; appName: string, plan?: Plan, plans?: Plan[], isLoadingPlans: boolean, loadingPlansErrorMessage?: string, onPlanClick?: (plan: Plan) => void, isOwner: boolean}) => {
  const { currentUser, appId, appName, plan: currentPlan, isLoadingPlans, loadingPlansErrorMessage, plans, onPlanClick, isOwner } = props;

  let planRAM = '';
  if (currentPlan?.maxRAM) {
    planRAM = currentPlan.maxRAM >= 1024 ? `${currentPlan.maxRAM / 1024} GB` : `${currentPlan.maxRAM} MB`;
  }

  return <div className="flex flex-col">
    <SettingsSection eyebrow="identity">
      {/* Read-only, and it always has been: there is no rename mutation in the
          API. The field is here because this is where you come to check the
          name, not to change it. */}
      <div className="max-w-[420px]">
        <TextInput
          value={appName}
          onChange={() => {}}
          label="Name"
          isDisabled={true}
          helpText="Shown across the dashboard."
        />
      </div>
    </SettingsSection>

    <Collaborators currentUser={currentUser} appId={appId} isOwner={isOwner} />

    <SettingsSection
      eyebrow="plan"
      meta={currentPlan ? `$${currentPlan.price}.00 / month` : undefined}
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 min-w-0">
        <span className="font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white">
          {currentPlan?.name}
        </span>
        <span className="font-courrier text-ds-115 text-navy-200">
          {[planRAM, currentPlan?.maxCPU ? `${currentPlan.maxCPU} vCPU` : undefined].filter(Boolean).join(' · ')}
        </span>
      </div>
      {currentPlan?.tagline ? (
        <p className="m-0 mt-2 text-ds-135 leading-[1.5] text-navy-200">{currentPlan.tagline}</p>
      ) : null}

      {isLoadingPlans ? (
        <div className="flex justify-center items-center py-10"><LoadingSpinner color='text-white' /></div>
      ) : loadingPlansErrorMessage ? (
        <div className="mt-4"><ErrorMessage message={loadingPlansErrorMessage} /></div>
      ) : (
          <div className="mt-4 rounded-container border border-navy-500 overflow-x-auto b4-thin">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-navy-500">
                  <th className="px-4 py-2.5 text-left b4-eyebrow-tight font-normal">Plan</th>
                  <th className="px-4 py-2.5 text-left b4-eyebrow-tight font-normal">CPU</th>
                  <th className="px-4 py-2.5 text-left b4-eyebrow-tight font-normal">RAM</th>
                  <th className="px-4 py-2.5 text-left b4-eyebrow-tight font-normal">Transfer</th>
                  <th className="px-4 py-2.5 text-left b4-eyebrow-tight font-normal">Price</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-500 text-ds-135">
                {plans?.map((plan) => (
                  <tr key={plan.id} className="hover:bg-navy-700/60 transition-colors duration-[120ms]">
                    <td className="px-4 py-3 text-white font-medium">{plan.name}</td>
                    <td className="px-4 py-3 font-courrier text-ds-115 text-navy-100">{plan.maxCPU}</td>
                    <td className="px-4 py-3 font-courrier text-ds-115 text-navy-100 min-w-[100px]">
                      {typeof plan.maxRAM === 'number'
                        ? plan.maxRAM >= 1024
                          ? `${(plan.maxRAM / 1024).toFixed(0)} GB`
                          : `${plan.maxRAM} MB`
                        : plan.maxRAM
                      }
                    </td>
                    <td className="px-4 py-3 font-courrier text-ds-115 text-navy-100">
                      {typeof plan.transferLimit === 'number'
                        ? plan.transferLimit >= 1000
                          ? `${(plan.transferLimit / 1000).toFixed(0)} TB`
                          : `${plan.transferLimit} GB`
                        : plan.transferLimit
                      }
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="font-semibold text-white">${plan.price}</span>
                      <span className="text-navy-300"> / month</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {plan.id === currentPlan?.id ? (
                        <span className="font-courrier text-ds-105 uppercase tracking-[0.08em] text-navy-300">current</span>
                      ) : (
                        <DsButton size="md" onClick={() => onPlanClick?.(plan)}>Change plan</DsButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
      )}
    </SettingsSection>
  </div>
}

const BuildAndDeployForm = (props: {sourceKind?: string, imageRepository?: string, imageTag?: string, branchName: string, rootDir: string, repositoryId: string, autoDeploy: boolean, exposedPort: string, startCommand?: string, builderPreference?: string, runtimeVersion?: string, isStaticSite?: boolean, isSubmitting: boolean, handleSubmit: (newValue: AppForm) => {}, plan?: Plan, isFreePlan: boolean, canWrite: boolean, canRead: boolean }) => {
  const { sourceKind, imageRepository, imageTag, branchName, rootDir, repositoryId, autoDeploy, exposedPort, startCommand, builderPreference, runtimeVersion, isStaticSite, isSubmitting, handleSubmit, isFreePlan, canWrite, canRead } = props;

  // An app running a published image is not built here and has no repository:
  // there is no Dockerfile to choose over buildpacks, no branch, no root
  // directory, and no push to auto-deploy from. Showing those controls asked
  // the user to configure a build that never runs — and Branch sat there empty
  // and required-looking, as if the app were misconfigured.
  const isImageSource = sourceKind === 'DOCKER_HUB';
  const [isBrowsingRepository, setIsBrowsingRepository] = useState(false);
  // Runs against the app's SAVED branch and folder, not the ones being edited
  // in this form: the status line describes the app as it stands, and keying it
  // to the inputs would fire a GitHub read on every keystroke.
  const [buildInfo, setBuildInfo] = useState<RepositoryBuildInfo | undefined>();

  useEffect(() => {
    if (!repositoryId || !branchName) {
      return;
    }

    let cancelled = false;

    back4app2
      .repositoryBuildInfo(repositoryId, branchName, rootDir || './')
      .then((result) => {
        if (!cancelled) {
          setBuildInfo(result);
        }
      })
      // A detection that fails costs the status line and nothing else — the
      // field still saves. Settings must not break because GitHub is slow.
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [repositoryId, branchName, rootDir]);

  const [formFields, setFormFields] = useState(() => (
    {
      branchName: {
        value: branchName || '',
        isValid: true,
        isRequired: true,
        errorMsg: ''
      },
      rootDir: {
        value: rootDir || '',
        isValid: true,
        isRequired: true,
        errorMsg: ''
      },
      exposedPort: {
        value: exposedPort || '',
        isValid: true,
        isRequired: false,
        errorMsg: ''
      },
      startCommand: { value: startCommand || '' },
      runtimeVersion: { value: runtimeVersion || '' },
      // Apps created before the Build Method existed have no stored value:
      // they were required to have a Dockerfile, so NULL reads as DOCKERFILE.
      buildMethod: { value: (builderPreference === 'BUILDPACK' ? 'BUILDPACK' : 'DOCKERFILE') as BuildMethod },
      autoDeploy: !!autoDeploy
    }
  ));

  const handleFormFields = (fieldName: MyAppSettingsFieldName, fieldValue: string) => {
    const newState = { ...formFields };
    if (fieldName === MyAppSettingsFieldName.BRANCH_NAME) {
      if (fieldValue === '') {
        newState.branchName.isValid = false;
        newState.branchName.errorMsg = 'Repository branch name is required!'
      } else {
        newState.branchName.errorMsg = '';
        newState.branchName.isValid = true;
      }
      newState.branchName.value = fieldValue;
    } else if (fieldName === MyAppSettingsFieldName.ROOT_DIR) {
      if (!fieldValue.startsWith('./')) {
        if (fieldValue.startsWith('.') || fieldValue.startsWith('/')) {
          fieldValue = fieldValue.substring(1);
        }
        fieldValue = `./${fieldValue}`;
      }
      newState.rootDir.value = fieldValue;
    } else if (fieldName === MyAppSettingsFieldName.EXPOSED_PORT) {
      if (!fieldValue) {
        newState.exposedPort.value = fieldValue;
        return setFormFields({...newState});
      }
      let val = +fieldValue;
      newState.exposedPort.value = fieldValue;
      newState.exposedPort.errorMsg = '';
      newState.exposedPort.isValid = true;
      if ((isNaN(val) || !(Number.isInteger(val) && val > 0 && val <= 65535)) && val !== undefined) {
        newState.exposedPort.errorMsg = 'Please provide a valid TCP port number (1-65535).'
        newState.exposedPort.isValid = false;
        newState.exposedPort.value = ''
      }
    }
    setFormFields({...newState})
  }

  const onSubmit = () => {
    const newValues = {
      branchName: formFields.branchName.value,
      rootDir: formFields.rootDir.value,
      autoDeploy: formFields.autoDeploy,
      exposedPort: +formFields.exposedPort.value,
      startCommand: formFields.startCommand.value,
      builderPreference: formFields.buildMethod.value,
      runtimeVersion: formFields.runtimeVersion.value
    }

    // The API rejects anything that is not a version, and the field already
    // says so — don't turn a typo into a failed save.
    if (newValues.runtimeVersion && !isValidRuntimePin(newValues.runtimeVersion)) {
      return;
    }

    handleSubmit(newValues);
  }

  const initialFields = {
    branchName: branchName || '',
    rootDir: rootDir || '',
    exposedPort: exposedPort || '',
    startCommand: startCommand || '',
    runtimeVersion: runtimeVersion || '',
    buildMethod: (builderPreference === 'BUILDPACK' ? 'BUILDPACK' : 'DOCKERFILE') as BuildMethod,
    autoDeploy: !!autoDeploy,
  };

  const dirtyCount = [
    formFields.branchName.value !== initialFields.branchName,
    formFields.rootDir.value !== initialFields.rootDir,
    formFields.exposedPort.value !== initialFields.exposedPort,
    (formFields.startCommand.value || '') !== initialFields.startCommand,
    (formFields.runtimeVersion.value || '') !== initialFields.runtimeVersion,
    formFields.buildMethod.value !== initialFields.buildMethod,
    formFields.autoDeploy !== initialFields.autoDeploy,
  ].filter(Boolean).length;

  if (!canRead) {
    return <GhostMessage customMessage="You don't have permission to access this feature." />;
  }

  return <div className="flex flex-col">
    {/* Browsing commits only on "Use this folder", so opening it here never
        edits the app by itself — the same contract as the create form. The
        branch comes from the field as it stands, not from what was saved, so
        the listing follows a branch the user just typed. */}
    {repositoryId && (
      <RepositoryBrowser
        open={isBrowsingRepository}
        repositoryId={repositoryId}
        branchName={formFields.branchName.value}
        initialPath={formFields.rootDir.value || ROOT_PATH}
        onClose={() => setIsBrowsingRepository(false)}
        onSelect={(path) => {
          handleFormFields(MyAppSettingsFieldName.ROOT_DIR, path);
          setIsBrowsingRepository(false);
        }}
      />
    )}
    {/* Unlike the create form, Dockerfile is NOT disabled when the repository
        has none: the user may be about to commit one, and the Build Method is
        explicit app configuration — the screen doesn't decide for them. */}
    <SettingsSection eyebrow="source">
      <div className="flex flex-col gap-6">
    {isImageSource ? (
      // Read-only on purpose: this screen saves settings, and changing the
      // image an app runs is a deploy, not a setting. Naming it here still
      // answers the question the tab now raises — "built from what?" — which
      // an empty Build & Deploy screen would leave hanging.
      <div className="space-y-2">
        <div className="font-medium leading-140">Image</div>
        <div className="text-sm opacity-70 truncate">
          {imageRepository ? `${imageRepository}:${imageTag || 'latest'}` : 'Docker Hub'}
        </div>
      </div>
    ) : (
      <div className="space-y-2">
        {/* Same shape as every other label on this screen. TextInput renders
            its label `font-medium leading-140` at the base size; this one was a
            plain 14px div, so the one control without a TextInput was also the
            one whose label looked like body text. */}
        <div className="font-medium leading-140">Build Method</div>
        <BuildMethodSelector
          value={formFields.buildMethod.value}
          onChange={(value) => setFormFields(prev => ({ ...prev, buildMethod: { value } }))}
          isDisabled={isSubmitting || !canWrite}
          groupName="settingsBuildMethod"
        />
      </div>
    )}
    {/* Its own field, not the tail of the Build Method block: nested in there
        it inherited the 8px label-to-control gap and sat glued under the
        radio cards, reading as part of them rather than as the next question.
        Only on the buildpack path — a Dockerfile names its own runtime in its
        FROM line. */}
    {formFields.buildMethod.value === 'BUILDPACK' && (
      <RuntimeVersionField
        detectedStack={buildInfo?.detectedStack}
        resolved={buildInfo?.runtimeVersion}
        value={formFields.runtimeVersion.value}
        onChange={(value) => setFormFields(prev => ({ ...prev, runtimeVersion: { value } }))}
        disabled={isSubmitting || !canWrite}
      />
    )}
    {!isImageSource && <TextInput
      value={formFields.branchName.value}
      label="Branch"
      name="branch" 
      onChange={(e) => handleFormFields(MyAppSettingsFieldName.BRANCH_NAME, e.target.value.trim())} 
      placeholderText="Branch"
      infoText="The repository branch that your web application uses."
      isError={!formFields.branchName.isValid}
      errorMessage={formFields.branchName.errorMsg}
      isDisabled={isSubmitting || !canWrite}
    />}
    {!isImageSource && <TextInput 
      value={formFields.rootDir.value} 
      label="Root directory" 
      name="rootDirPath" 
      onChange={(e) => handleFormFields(MyAppSettingsFieldName.ROOT_DIR, e.target.value.trim())} 
      placeholderText="./"
      infoText="By default, the root directory is the repository's root. If you specify a different directory, Back4app runs all commands within that directory only and ignores any changes outside it."
      isError={!formFields.rootDir.isValid}
      errorMessage={formFields.rootDir.errorMsg}
      isDisabled={isSubmitting || !canWrite}
      // Never disabled while the user can write: "no subfolders" is only
      // knowable after a listing, and an empty listing is also what a mistyped
      // path produces — exactly when the browser is the way out. The modal
      // answers in words; a dead control explains nothing.
      endAdornment={
        repositoryId && canWrite ? (
          <button
            type="button"
            className="b4-focus inline-flex text-action-soft hover:text-white text-ds-125 font-semibold items-center whitespace-nowrap"
            onClick={() => setIsBrowsingRepository(true)}
          >
            <FolderIcon className="mr-[0.625rem]" /> Browse folders
          </button>
        ) : undefined
      }
    />}
      </div>
    </SettingsSection>

    {!isImageSource ? (
      <SettingsSection eyebrow="deploy">
      <ToggleSwitchCheckbox 
        value={formFields.autoDeploy} 
        onChange={() => setFormFields(prev => ({...prev, autoDeploy: !prev.autoDeploy}))} 
        labelText="Autodeploy" 
        description="Deploy on every repository push. Off means you trigger every deploy yourself."
        disabled={isFreePlan || !canWrite}
        infoText={isFreePlan ? "This feature is available on paid plans. Upgrade your plan to automatically deploy your application whenever changes are pushed to your repository.": null}
      />
      </SettingsSection>
    ) : null}

    <SettingsSection eyebrow="runtime">
      <div className="flex flex-col gap-6">
    <TextInput 
      value={`${formFields.exposedPort.value}`} 
      label="Port" 
      name="exposedPort" 
      onChange={(e) => handleFormFields(MyAppSettingsFieldName.EXPOSED_PORT, e.target.value.trim())} 
      placeholderText="8080"
      infoText="The port your app listens on. Leave it empty and Back4app detects it automatically."
      isError={!formFields.exposedPort.isValid}
      errorMessage={formFields.exposedPort.errorMsg}
      isDisabled={!canWrite}
    />
    {/* The whole block — label, input and the static-site line — belongs to the
        buildpack route and is hidden on Dockerfile, matching the create form.
        Nothing here is read by the kaniko path: `start_command` is consumed
        only by the CNB shim, and `is_static_site` only by the CNB entrypoint,
        so on Dockerfile these controls describe something that cannot happen.

        Hiding does NOT lose the saved command. `formFields` lives in this
        component, not inside TextInput, so unrendering the input leaves
        `formFields.startCommand.value` untouched; onSubmit still sends the
        value it read from the app, handleSubmit finds no difference, and
        nothing is written. Switching back to Buildpacks shows it again. */}
    {formFields.buildMethod.value === 'BUILDPACK' && (
      <>
        <TextInput
          value={formFields.startCommand.value}
          label="Start Command"
          name="startCommand"
          onChange={(e) => setFormFields(prev => ({ ...prev, startCommand: { value: e.target.value } }))}
          placeholderText="e.g. gunicorn app:app"
          infoText="Optional. Leave empty and we'll use your Procfile or start script. Set it when your app has neither. The app must listen on the PORT env var (or set the Port above)."
          isError={false}
          errorMessage=""
          isDisabled={!canWrite}
        />
        {/* Static serving is detected once, when the app is created. A
            repository that later grows a server would keep serving its build
            output with the server never running — so the way out has to be
            visible, and it is the Start Command right above rather than one
            more switch to explain.

            Gone once a command is set, because by then the sentence is false:
            `cnbBuild` reads `is_static_site` only for an empty command, so the
            stored flag is dormant and the app is served by the command above.
            It comes back if the field is cleared, which is exactly what it
            promises. */}
        {isStaticSite && !formFields.startCommand.value && (
          <div className="text-sm opacity-70 -mt-4">
            This app is served as a static site. Set a Start Command to run a server instead.
          </div>
        )}
      </>
    )}
      </div>
    </SettingsSection>

    <SettingsSaveBar
      dirtyCount={dirtyCount}
      isSubmitting={isSubmitting}
      canSave={canWrite
        && formFields.branchName.isValid
        && formFields.rootDir.isValid
        && formFields.exposedPort.isValid
        && (!formFields.runtimeVersion.value || isValidRuntimePin(formFields.runtimeVersion.value))}
      onSave={() => {
        if (!canWrite && canRead) {
          toast.error('You don\'t have permission to save build and deploy settings.', {
            className: TOAST_SURFACE,
            icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
            duration: 4000
          });
          return;
        }
        onSubmit();
      }}
      onDiscard={() => setFormFields({
        branchName: { value: initialFields.branchName, isValid: true, isRequired: true, errorMsg: '' },
        rootDir: { value: initialFields.rootDir, isValid: true, isRequired: true, errorMsg: '' },
        exposedPort: { value: initialFields.exposedPort, isValid: true, isRequired: false, errorMsg: '' },
        startCommand: { value: initialFields.startCommand },
        runtimeVersion: { value: initialFields.runtimeVersion },
        buildMethod: { value: initialFields.buildMethod },
        autoDeploy: initialFields.autoDeploy,
      })}
    />
  </div>
}

const HealthAndAlertForm = (props: {healthCheckEndpoint: string, isSubmitting: boolean, handleSubmit: (newValue: AppForm) => {}, canWrite: boolean, canRead: boolean }) => {
  const { healthCheckEndpoint, isSubmitting, handleSubmit, canWrite, canRead } = props;
  const [formFields, setFormFields] = useState(() => (
    {
      healthCheckEndpoint: {
        value: healthCheckEndpoint || '',
        isValid: true,
        isRequired: true,
        errorMsg: ''
      }
    }
  ));

  const handleFormFields = (fieldName: MyAppSettingsFieldName, fieldValue: string) => {
    const newState = { ...formFields };
    if (fieldName === MyAppSettingsFieldName.HEALTH_CHECK_ENDPOINT) {
      if (fieldValue[0] !== '/') {
        fieldValue = `/${fieldValue}`
      }
      newState.healthCheckEndpoint.value = fieldValue;
    }
    setFormFields({...newState})
  }

  const onSubmit = () => {
    const newValues = {
      healthCheckEndpoint: formFields.healthCheckEndpoint.value,
    }
    handleSubmit(newValues);
  }

  if (!canRead) {
    return <GhostMessage customMessage="You don't have permission to access this feature." />;
  }

  const isDirty = formFields.healthCheckEndpoint.value !== (healthCheckEndpoint || '');

  return <div className="flex flex-col">
    <SettingsSection eyebrow="health check">
      <div className="max-w-[420px]">
        <TextInput
          value={formFields.healthCheckEndpoint.value}
          label="Health check path"
          name="healthCheckEndpoint"
          onChange={(e) => handleFormFields(MyAppSettingsFieldName.HEALTH_CHECK_ENDPOINT, e.target.value.trim())}
          placeholderText="/checkpath"
          infoText="An HTTP request performed during deployments to confirm that a service is healthy."
          isError={!formFields.healthCheckEndpoint.isValid}
          errorMessage={formFields.healthCheckEndpoint.errorMsg}
          isDisabled={isSubmitting || !canWrite}
        />
      </div>
    </SettingsSection>

    <SettingsSaveBar
      dirtyCount={isDirty ? 1 : 0}
      isSubmitting={isSubmitting}
      canSave={canWrite && formFields.healthCheckEndpoint.isValid}
      onSave={() => {
        if (!canWrite && canRead) {
          toast.error('You don\'t have permission to save health check settings.', {
            className: TOAST_SURFACE,
            icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
            duration: 4000
          });
          return;
        }
        onSubmit();
      }}
      onDiscard={() => setFormFields({
        healthCheckEndpoint: { value: healthCheckEndpoint || '', isValid: true, isRequired: true, errorMsg: '' }
      })}
    />
  </div>
}

const VariablesForm = (props: { envVars: { id: string, key: string; value?: string }[], isSubmitting: boolean, handleSubmit: (newValues: AppForm) => {}, canWrite: boolean, canRead: boolean }) => {
  const { envVars, isSubmitting, handleSubmit, canWrite, canRead } = props;
  const initialFields = useMemo(() => envVars.map(env => ({
    id: Math.random().toString(16).slice(2),
    name: { isValid: true, value: env.key, errorMsg: '' },
    value: { isValid: true, value: env.value, showValue: false, errorMsg: '' }
  })), [envVars]);
  const [formFields, setFormFields] = useState(initialFields);
  /** The one row currently open for editing; the rest stay as read rows. */
  const [editingId, setEditingId] = useState<string | null>(null);

  // Renames count twice — once as a variable that no longer exists, once as one
  // that did not. That is what happened, and the bar only has to say "how many
  // things did I touch", not reconstruct the diff.
  const dirtyCount = useMemo(() => {
    const before = new Map(envVars.map(env => [env.key, env.value ?? '']));
    const after = new Map(formFields.map(env => [env.name.value, env.value.value ?? '']));
    let count = 0;
    after.forEach((value, key) => {
      if (!before.has(key) || before.get(key) !== value) {
        count += 1;
      }
    });
    before.forEach((_value, key) => {
      if (!after.has(key)) {
        count += 1;
      }
    });
    return count;
  }, [envVars, formFields]);

  const validateEnvVariable = (name: string, value = '') => {
    const isCombinedLengthValid = name.length + value.length <= ENV_VARIABLE_MAX_LENGTH;
    const isValid = !!name.match(/(^[A-Z_])[A-Z0-9_]+$/);
    return {
      name: {
        isValid: isValid && isCombinedLengthValid,
        errorMsg: isValid ? '' : 'Substitution variables must begin with an underscore (_) or uppercase letter and use only uppercase letters and numbers'
      },
      value: {
        isValid: isCombinedLengthValid,
        errorMsg: isCombinedLengthValid ? '' : `The combined length of the variable name and value must not exceed ${ENV_VARIABLE_MAX_LENGTH} characters.`
      }
    };
  };

  const handleOnChangeEnvVars = (id: string, newValue: string, type: 'name' | 'value') => {
    const newEnvVariableList = formFields.map(env => {
      if (id === env.id && type === 'name') {
        const validation = validateEnvVariable(newValue, env.value.value);
        return {
          ...env,
          name: { value: newValue, ...validation.name },
          value: { ...env.value, ...validation.value }
        }
      } else if (id === env.id && type === 'value') {
        const validation = validateEnvVariable(env.name.value, newValue);
        return {
          ...env,
          name: { ...env.name, ...validation.name },
          value: { ...env.value ,value: newValue, ...validation.value }
        }
      }
      return env;
    });
    setFormFields([...newEnvVariableList]);
  }

  const handleRemoveEnviromentVar = (id: string) => {
    const newEnvVariableList = formFields.filter(env => env.id !== id);
    setFormFields(newEnvVariableList);
  }

  const toggleEnvVariableVisibilty = (id: string) => {
    const newEnvVariableList = formFields.map(env => {
      if (env.id === id) {
        return { ...env, value: {...env.value, showValue: !env.value.showValue} }
      }
      return env;
    });
    setFormFields(newEnvVariableList);
  }

  const onSubmit = () => {
    const validatedFields = formFields.map(env => {
      const validation = validateEnvVariable(env.name.value, env.value.value);
      return {
        ...env,
        name: { ...env.name, ...validation.name },
        value: { ...env.value, ...validation.value }
      };
    });
    const inValidEnv = validatedFields.findIndex(env => !env.name.isValid || !env.value.isValid);
    if (formFields.length && inValidEnv !== -1) {
      setFormFields(validatedFields);
      return;
    }
    const envs = formFields.map(env => ({
      key: env.name.value,
      value: env.value.value
    }));
    handleSubmit({ envs });
  }

  if (!canRead) {
    return <GhostMessage customMessage="You don't have permission to access this feature." />;
  }

  const denied = (what: string) => {
    toast.error(`You don't have permission to ${what} environment variables.`, {
      className: TOAST_SURFACE,
      icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
      duration: 4000
    });
  };

  const startAdding = () => {
    if (!canWrite) {
      denied('add');
      return;
    }
    const id = Math.random().toString(16).slice(2);
    setFormFields(prev => [...prev, {
      id,
      name: { isValid: true, value: '', errorMsg: '' },
      value: { isValid: true, value: '', showValue: true, errorMsg: '' },
    }]);
    setEditingId(id);
  };

  return <div className="flex flex-col gap-4">
    {/* The head sits on the page, not inside the box: it names the table and
        acts on it, and the card's edge belongs to the rows. An eyebrow rather
        than a heading — the page title above already says "Environment
        variables", and every other tab names its groups this way. */}
    <div className="flex items-center justify-between gap-3">
      <span className="flex items-baseline gap-2">
        <Eyebrow>variables</Eyebrow>
        <span className="font-courrier text-ds-115 text-navy-300">{formFields.length}</span>
      </span>
      <DsButton size="md" onClick={startAdding}>
        <PlusIcon className="w-3 h-3" />Add variable
      </DsButton>
    </div>

    <SurfaceCard tone="surface" radius="container">
      {formFields.length === 0 ? (
        <div className="px-4 py-10 lg:px-5 text-center text-ds-135 text-navy-300">
          No variables yet.
        </div>
      ) : formFields.map((enviromentVar) => {
        const { id, name, value } = enviromentVar;
        const envValue = value.value || '';
        const isEditing = editingId === id;

        if (isEditing) {
          return (
            <div key={id} className="border-t border-navy-500 first:border-t-0 px-4 py-4 lg:px-5 flex flex-col gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <TextInput
                  value={name.value}
                  label="Name"
                  name={`env-name-${id}`}
                  onChange={(e) => handleOnChangeEnvVars(id, e.target.value.trim(), 'name')}
                  placeholderText="EXAMPLE_NAME"
                  isError={!name.isValid}
                  errorMessage={name.errorMsg}
                  isDisabled={!canWrite}
                />
                <TextInput
                  type={value.showValue ? 'text' : 'password'}
                  value={envValue}
                  label="Value"
                  name={`env-value-${id}`}
                  onChange={(e) => handleOnChangeEnvVars(id, e.target.value, 'value')}
                  placeholderText="A1B2C3D4E5F6"
                  endAdornment={
                    <span className="cursor-pointer" onClick={() => toggleEnvVariableVisibilty(id)}>
                      {value.showValue ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </span>
                  }
                  isError={!value.isValid}
                  errorMessage={value.errorMsg}
                  isDisabled={!canWrite}
                />
              </div>
              <div className="flex items-center gap-2">
                <DsButton
                  size="md"
                  onClick={() => setEditingId(null)}
                  isDisabled={!name.isValid || !value.isValid || !name.value}
                >
                  Done
                </DsButton>
                <button
                  type="button"
                  className="b4-focus rounded-control px-2 h-[30px] text-ds-125 font-semibold text-state-err hover:bg-state-err/10 transition-colors duration-[120ms]"
                  onClick={() => {
                    if (!canWrite) { denied('delete'); return; }
                    setEditingId(null);
                    handleRemoveEnviromentVar(id);
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
          );
        }

        return (
          <div
            key={id}
            className="group border-t border-navy-500 first:border-t-0 flex items-center gap-3 px-4 lg:px-5 hover:bg-navy-700/50 transition-colors duration-[120ms]"
          >
            {/* The row is the edit affordance. A pencil per row on a screen
                that is nothing but rows is five pencils saying the same thing. */}
            <button
              type="button"
              className="b4-focus flex-1 min-w-0 flex flex-col gap-1 py-3 text-left sm:flex-row sm:items-center sm:gap-4"
              onClick={() => canWrite ? setEditingId(id) : denied('edit')}
            >
              <span className="sm:w-[38%] font-courrier text-ds-135 text-white truncate">
                {name.value || <span className="text-navy-300">unnamed</span>}
              </span>
              <span className="flex-1 min-w-0 font-courrier text-ds-135 text-navy-100 truncate">
                {value.showValue ? envValue : '•'.repeat(Math.min(Math.max(envValue.length, 8), 16))}
              </span>
            </button>
            <button
              type="button"
              aria-label={value.showValue ? `Hide the value of ${name.value}` : `Show the value of ${name.value}`}
              className="b4-focus flex-none rounded-control p-1.5 text-navy-300 hover:text-white transition-colors duration-[120ms]"
              onClick={() => toggleEnvVariableVisibilty(id)}
            >
              {value.showValue ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </button>
            <button
              type="button"
              aria-label={`Delete ${name.value}`}
              className="b4-focus flex-none rounded-control p-1.5 text-navy-300 hover:text-state-err transition-colors duration-[120ms]"
              onClick={() => canWrite ? handleRemoveEnviromentVar(id) : denied('delete')}
            >
              <TrashIcon />
            </button>
          </div>
        );
      })}

      <div className="border-t border-navy-500 px-4 py-3.5 lg:px-5 text-ds-135 text-navy-300">
        Changing a variable takes effect on the next deploy.
      </div>
    </SurfaceCard>

    <SettingsSaveBar
      dirtyCount={dirtyCount}
      isSubmitting={isSubmitting}
      canSave={canWrite && formFields.every(env => env.name.isValid && env.value.isValid)}
      onSave={() => canWrite ? onSubmit() : denied('save')}
      onDiscard={() => { setEditingId(null); setFormFields(initialFields); }}
    />
  </div>
};

const SuspendDeleteForm = (props: { appName: string, appId: string, block?: AppBlock, updateIsDeletingApp: (isDeleting: boolean) => void }) => {
  const { appName, appId, block, updateIsDeletingApp } = props;
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteAppErrorMessage, setDeleteAppErrorMessage] = useState('');
  const [verifyAppNameInput, setVerifyAppNameInput] = useState(() => ({
    value: '',
    isError: false,
    isValid: true,
    isTouched: false,
    errorMsg: ''
  }));

  const isConfirmed = verifyAppNameInput.value === appName && !!appName;

  const onChangeDeleteAppInput = (val: string) => {
    let isValid=true, errorMsg='';
    if (val !== appName) {
      isValid = false; errorMsg = 'App name entered does not match!';
    }
    setVerifyAppNameInput({
      ...verifyAppNameInput,
      value: val,
      isValid,
      errorMsg,
      isTouched: true
    });
  };

  const handleDeleteApp = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!isConfirmed) {
      return;
    }
    setIsDeleting(true);
    updateIsDeletingApp(true);
    trackEvent(AmplitudeEvent.CLICK_ON_DELETE_APP);
    try {
      await back4app2.deleteApp(appId as string);
      updateIsDeletingApp(false);
      window.location.href = `${BACK4APP_DOT_COM_DASHBOARD_URL}/apps`;
    } catch (err) {
      if (err instanceof NetworkError) {
        setDeleteAppErrorMessage('Network error when deleting app. Please check your internet connection or try again later.');
      } else if (err instanceof AuthenticationError || err instanceof AuthorizationError) {
        window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
      } else if (err instanceof BlockedResourceError) {
        // Refused on purpose, and permanently until the review ends. "Try
        // again later" would be a lie the customer could act on all day.
        // Reachable even with the button guarded below, when the block lands
        // between this page loading and the click.
        setDeleteAppErrorMessage(err.message);
      } else {
        console.error('error deleting app', err);
        setDeleteAppErrorMessage('Something went wrong, please try again later.');
      }
    } finally {
      setIsDeleting(false);
    }
  }

  return <div className="flex flex-col gap-4">
    {/* One card, because Delete is the only thing this app can actually do
        here — suspend and transfer-ownership are not in the API, and a
        disabled card promising them would be a worse answer than their
        absence. The border carries the warning; the page title already says
        "Danger zone". */}
    <div className="rounded-container border border-state-err/40 bg-navy-800 p-4 lg:p-5 flex flex-col gap-3">
      <span className="font-sora text-ds-15 font-semibold tracking-[-0.01em] text-state-err">
        Delete this web app
      </span>
      {/* Said before the ritual, not after it. Letting someone type their
          app's name to confirm a deletion we will refuse is a worse way to
          deliver the same no. */}
      <span className="text-ds-135 leading-[1.55] text-navy-100">
        {block
          ? "This app is under security review and can't be deleted while the review is open."
          : 'Every resource stops immediately and cannot be recovered. Type the app name to confirm.'}
      </span>

      {!block ? (<>
        {/* The typing is the confirmation. A modal that asks for the same
            string a second time is a speed bump, not a safeguard. */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="w-full sm:max-w-[320px]">
            <TextInput
              value={verifyAppNameInput.value}
              name="confirmDeleteAppName"
              onChange={(e) => onChangeDeleteAppInput(e.target.value.trim())}
              placeholderText={appName}
              isDisabled={isDeleting}
            />
          </div>
          <button
            type="button"
            onClick={handleDeleteApp}
            disabled={!isConfirmed || isDeleting}
            className={`b4-focus flex-none inline-flex items-center justify-center h-11 sm:h-[38px] px-4 rounded-surface text-[14px] font-semibold transition-colors duration-[120ms] ${
              isConfirmed && !isDeleting
                ? 'border border-state-err/60 bg-transparent text-state-err hover:bg-state-err/10 hover:border-state-err cursor-pointer'
                : 'border border-transparent bg-navy-600 text-navy-300 cursor-not-allowed'
            }`}
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
        {/* Only once they have typed something wrong — an error under an empty
            field is a complaint about not having started yet. */}
        {verifyAppNameInput.isTouched && !isConfirmed ? (
          <span className="text-ds-115 text-state-err">{verifyAppNameInput.errorMsg}</span>
        ) : null}
      </>) : null}

      {deleteAppErrorMessage ? (
        <span className="text-ds-115 text-state-err">{deleteAppErrorMessage}</span>
      ) : null}
    </div>
  </div>
}

export default MyAppSettings
